import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import subscriptionService from '../../services/subscription-service';
import productService from '../../db/product-service';
import freeTrialService from '../../services/free-trial-service';
import './SubscriptionCustomizer.scss';

const SubscriptionCustomizer = () => {
  const { planType } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [shots, setShots] = useState([]);
  const [smoothies, setSmoothies] = useState([]);
  const [deliveryDates, setDeliveryDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trialAlreadyClaimed, setTrialAlreadyClaimed] = useState(false);
  const isAuthenticated = sessionStorage.getItem('isUserAuthenticated') === 'true';
  const customerId = sessionStorage.getItem('customerId');
  const isTrial = planType === 'free_trial';

  useEffect(() => {
    if (!isAuthenticated || !customerId) {
      navigate('/login');
      return;
    }
    if (isTrial) {
      freeTrialService.getTrialStatus().then(status => {
        if (status.claimed === true) setTrialAlreadyClaimed(true);
      }).catch(() => {});
    }
    loadPlanAndProducts();
  }, [planType, isAuthenticated, customerId]);

  const generateWeekdayDates = (count) => {
    const dates = [];
    const cursor = new Date();
    cursor.setDate(cursor.getDate() + 1);
    while (dates.length < count) {
      if (cursor.getDay() !== 0 && cursor.getDay() !== 6) {
        dates.push(new Date(cursor));
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return dates;
  };

  const loadPlanAndProducts = async () => {
    try {
      const [plansData, productsData] = await Promise.all([
        subscriptionService.getSubscriptionPlans(),
        productService.getAllProducts()
      ]);

      const currentPlan = plansData.find(p => p.planType === planType);
      setPlan(currentPlan);

      const shotProducts = productsData.filter(p => {
        const cats = Array.isArray(p.category) ? p.category : (p.category ? [p.category] : []);
        return !cats.includes('premium') &&
          (cats.includes('shots') || p.name.toLowerCase().includes('juice') || p.name.toLowerCase().includes('shot'));
      });
      const smoothieProducts = productsData.filter(p => {
        const cats = Array.isArray(p.category) ? p.category : (p.category ? [p.category] : []);
        return !cats.includes('premium') &&
          (cats.includes('smoothies') || p.name.toLowerCase().includes('smoothie'));
      });

      setShots(shotProducts);
      setSmoothies(smoothieProducts);
      setDeliveryDates(generateWeekdayDates(planType === 'free_trial' ? 5 : 20));
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const handleDateProductSelection = (dateKey, type, productId) => {
    setDateSelections(prev => ({ ...prev, [dateKey]: { ...prev[dateKey], [type]: productId } }));
  };

  const selectAllDates = (type, productId) => {
    const next = { ...dateSelections };
    deliveryDates.forEach(d => {
      const key = d.toISOString().split('T')[0];
      next[key] = { ...next[key], [type]: productId };
    });
    setDateSelections(next);
  };

  const selectColumn = (colIndex, type, productId) => {
    const next = { ...dateSelections };
    for (let i = colIndex; i < deliveryDates.length; i += 5) {
      const key = deliveryDates[i].toISOString().split('T')[0];
      next[key] = { ...next[key], [type]: productId };
    }
    setDateSelections(next);
  };

  const handleProceedToCheckout = () => {
    const missing = deliveryDates.filter(d => {
      const key = d.toISOString().split('T')[0];
      const sel = dateSelections[key];
      if (!sel?.shot) return true;
      if (!isTrial && plan?.smoothiesPerMonth > 0 && !sel?.smoothie) return true;
      return false;
    });

    if (missing.length > 0) {
      alert(isTrial
        ? 'Please select a shot for all 5 days'
        : `Please select products for all ${deliveryDates.length} dates`);
      return;
    }

    const deliverySchedule = {};
    deliveryDates.forEach(d => {
      const key = d.toISOString().split('T')[0];
      deliverySchedule[key] = { status: 'pending', ...dateSelections[key] };
    });

    navigate('/subscription/checkout', {
      state: { planType, selectedProducts: [], deliverySchedule }
    });
  };

  if (!isAuthenticated || !customerId) {
    return <div className="loading">Redirecting to login...</div>;
  }

  if (loading || !plan) {
    return (
      <div className="subscription-customizer">
        <div className="loading"><h2>Loading...</h2></div>
      </div>
    );
  }

  if (isTrial && trialAlreadyClaimed) {
    return (
      <div className="subscription-customizer">
        <div className="customizer-header">
          <h2>Welcome Gift Already Claimed</h2>
          <p>You have already claimed your free trial.</p>
          <button className="proceed-button" onClick={() => {
            navigate('/customer');
            setTimeout(() => window.dispatchEvent(new CustomEvent('setCustomerTab', { detail: 'subscriptions' })), 100);
          }}>
            View My Subscriptions
          </button>
        </div>
      </div>
    );
  }

  // Free trial — auto-assigned schedule, no selection needed
  if (isTrial) {
    // One unique shot per day — 5 shots for 5 days
      const autoSchedule = deliveryDates.reduce((acc, date, idx) => {
        const key = date.toISOString().split('T')[0];
        acc[key] = { shot: shots[idx]?._id || null };
        return acc;
      }, {});

      return (
        <div className="subscription-customizer">
          <div className="customizer-header">
            <h1>Your Welcome Gift Schedule</h1>
            <p className="plan-description">Try all 5 immunity shots — one new shot every weekday starting tomorrow</p>
          </div>

          <div className="trial-section">
            <div className="trial-days-grid">
              {deliveryDates.map((date, idx) => {
                const key = date.toISOString().split('T')[0];
                const shot = shots[idx];
                const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                return (
                  <div key={key} className="trial-day-slot assigned">
                    <div className="day-slot-header">
                      <span className="day-number">Day {idx + 1}</span>
                      <span className="day-date">{dayName}, {dateStr}</span>
                    </div>
                    {shot ? (
                      <div className="day-slot-assigned">
                        <img src={shot.image_url || 'https://via.placeholder.com/50'} alt={shot.name} />
                        <span>{shot.name}</span>
                      </div>
                    ) : (
                      <div className="day-slot-empty"><span>Shot TBD</span></div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="checkout-section">
            <button
              className="proceed-button"
              onClick={() => {
                const deliverySchedule = {};
                deliveryDates.forEach((date, idx) => {
                  const key = date.toISOString().split('T')[0];
                  deliverySchedule[key] = { status: 'pending', shot: shots[idx]?._id || null };
                });
                navigate('/subscription/checkout', {
                  state: { planType, selectedProducts: [], deliverySchedule }
                });
              }}
            >
              Proceed to Checkout
            </button>
          </div>
        </div>
      );
    }

  // Regular plans — 20-day view
  const selectAllProducts = () => {
    const next = { ...dateSelections };
    deliveryDates.forEach(d => {
      const key = d.toISOString().split('T')[0];
      next[key] = {};
      if (plan.shotsPerMonth > 0 && shots.length > 0) next[key].shot = shots[0]._id;
      if (plan.smoothiesPerMonth > 0 && smoothies.length > 0) next[key].smoothie = smoothies[0]._id;
    });
    setDateSelections(next);
  };

  return (
    <div className="subscription-customizer">
      <div className="customizer-header">
        <h1>Customize Your {plan.name}</h1>
        <p className="plan-description">Select products for each delivery date (20 weekdays)</p>
        <div className="global-select-all">
          <label>
            <input type="checkbox" onChange={e => { if (e.target.checked) selectAllProducts(); }} />
            <span>Select All 20 Days (First Product for Each Category)</span>
          </label>
        </div>
      </div>

      {plan.shotsPerMonth > 0 && (
        <div className="product-section">
          <h2>Select Immunity Shots for Each Date</h2>
          <div className="quick-select">
            <label>
              <input type="checkbox" onChange={e => { if (e.target.checked && shots.length > 0) selectAllDates('shot', shots[0]._id); }} />
              Select All (First Product)
            </label>
          </div>
          <div className="product-options">
            {shots.map(p => (
              <div key={p._id} className="product-option">
                <img src={p.image_url || 'https://via.placeholder.com/50'} alt={p.name} />
                <span>{p.name}</span>
                <button onClick={() => selectAllDates('shot', p._id)}>Select All</button>
                {[0, 1, 2, 3, 4].map(col => (
                  <button key={col} onClick={() => selectColumn(col, 'shot', p._id)}>Col {col + 1}</button>
                ))}
              </div>
            ))}
          </div>
          <div className="dates-grid">
            <div className="column-headers">
              {[1, 2, 3, 4, 5].map(col => (
                <div key={col} className="column-header">
                  <label>
                    <input type="checkbox" onChange={e => { if (e.target.checked && shots.length > 0) selectColumn(col - 1, 'shot', shots[0]._id); }} />
                    Col {col}
                  </label>
                </div>
              ))}
            </div>
            {deliveryDates.map((date, idx) => {
              const key = date.toISOString().split('T')[0];
              return (
                <div key={key} className="date-cell">
                  <div className="date-label">Day {idx + 1}<br />{date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                  <select value={dateSelections[key]?.shot || ''} onChange={e => handleDateProductSelection(key, 'shot', e.target.value)}>
                    <option value="">Select Shot</option>
                    {shots.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                  </select>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {plan.smoothiesPerMonth > 0 && (
        <div className="product-section">
          <h2>Select Smoothies for Each Date</h2>
          <div className="quick-select">
            <label>
              <input type="checkbox" onChange={e => { if (e.target.checked && smoothies.length > 0) selectAllDates('smoothie', smoothies[0]._id); }} />
              Select All (First Product)
            </label>
          </div>
          <div className="product-options">
            {smoothies.map(p => (
              <div key={p._id} className="product-option">
                <img src={p.image_url || 'https://via.placeholder.com/50'} alt={p.name} />
                <span>{p.name}</span>
                <button onClick={() => selectAllDates('smoothie', p._id)}>Select All</button>
                {[0, 1, 2, 3, 4].map(col => (
                  <button key={col} onClick={() => selectColumn(col, 'smoothie', p._id)}>Col {col + 1}</button>
                ))}
              </div>
            ))}
          </div>
          <div className="dates-grid">
            <div className="column-headers">
              {[1, 2, 3, 4, 5].map(col => (
                <div key={col} className="column-header">
                  <label>
                    <input type="checkbox" onChange={e => { if (e.target.checked && smoothies.length > 0) selectColumn(col - 1, 'smoothie', smoothies[0]._id); }} />
                    Col {col}
                  </label>
                </div>
              ))}
            </div>
            {deliveryDates.map((date, idx) => {
              const key = date.toISOString().split('T')[0];
              return (
                <div key={key} className="date-cell">
                  <div className="date-label">Day {idx + 1}<br />{date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                  <select value={dateSelections[key]?.smoothie || ''} onChange={e => handleDateProductSelection(key, 'smoothie', e.target.value)}>
                    <option value="">Select Smoothie</option>
                    {smoothies.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                  </select>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="checkout-section">
        <button className="proceed-button" onClick={handleProceedToCheckout}>
          Proceed to Checkout
        </button>
      </div>
    </div>
  );
};

export default SubscriptionCustomizer;
